import {defineStore} from 'pinia';
import {DataService} from '../../api/data/DataService';
import {actionToCard, sortActions} from 'hit-online-web-ui/src/helpers/action';
import {useConfigurationStore} from './configuration';
import {useUserProfileStore} from './userProfile';
import {HitActionUtils} from '@hit/components/src/utils/action/HitActionUtils';
import {ActionService} from '../../api';

export const useActionStore = defineStore('action', {
  state: () => ({
    assignedCount: null,
    assignedActions: [],
    actionFilters: null,
    filteredActionIds: [],
    filteredActions: [],
    activeActionId: null,
    actionStack: [],
    elapsedTime: 0,
    chronometerInterval: null,
    chronometerStartedAtMs: null,
    chronometerBaseDurationMs: 0,
    userStaffId: null,
    changingState: false,
  }),
  getters: {
    /**
     * When the changing state is set to true, we display spinners instead of play
     * icons to disable a second click while the changes are done in Action API
     */
    getChangingState(state) {
      return state.changingState;
    },
    filteredAssignedActions(state) {
      return state.assignedActions.filter((action) =>
        state.filteredActionIds.includes(action.id),
      );
    },
    activeCard(state) {
      return state.assignedActions.find(
        (action) => action.id === state.activeActionId,
      );
    },
    activeActionIsRunning() {
      return this.activeCard?.running_state === 'START' ?? false;
    },
    formattedElapsedTime(state) {
      const hours = Math.floor(state.elapsedTime / (1000 * 60 * 60));
      const minutes = Math.floor(
        (state.elapsedTime % (1000 * 60 * 60)) /
          (1000 * 60).toString().padStart(2, '0'),
      );
      const seconds = Math.floor((state.elapsedTime % (1000 * 60)) / 1000)
        .toString()
        .padStart(2, '0');
      return `${hours}h ${minutes}m ${seconds}s`;
    },
    actionsInStack(state) {
      const stack = [];
      state.actionStack.forEach((actionId) => {
        stack.push(
          state.assignedActions.find((action) => action.id === actionId),
        );
      });
      return stack;
    },
  },

  actions: {
    /**
     * Init method executed during mounting process of entire application
     */
    async initActionStore() {
      await this.updateAssignedActions();
    },

    /**
     * Checks if the user activated the time-tracking module to enable start/stop
     */
    hasTimeTrackingModule() {
      const trackingModule = useUserProfileStore().modules.find(
        (module) => module.description_key === 'action-time-tracking',
      );
      return trackingModule?.selected ?? false;
    },

    /**
     * Checks if the passed actionId is an action assigned to us
     */
    isMyAction(actionId) {
      return this.assignedActions.some((obj) => obj.id === actionId);
    },

    /**
     * Checks if the action can be started by the user
     */
    isStartableAction(actionId) {
      if (!this.hasTimeTrackingModule()) return false;
      const action = this.assignedActions.find(
        (action) => action.id === actionId,
      );
      if (!action) return false;
      return (
        !this.changingState && ['PAUSE', 'STOP'].includes(action.running_state)
      );
    },

    /**
     * Checks if the action can be paused by the user
     */
    isPausableAction(actionId) {
      if (!this.hasTimeTrackingModule()) return false;
      const action = this.assignedActions.find(
        (action) => action.id === actionId,
      );
      if (!action) return false;
      return !this.changingState && ['START'].includes(action.running_state);
    },

    /**
     * Checks if the action can be paused by the user
     */
    isStoppableAction(actionId) {
      if (!this.hasTimeTrackingModule()) return false;
      const action = this.assignedActions.find(
        (action) => action.id === actionId,
      );
      if (!action) return false;
      return (
        !this.changingState && ['START', 'PAUSE'].includes(action.running_state)
      );
    },

    /**
     * Assigns the action via the Action API
     */
    async assignAction(actionId, assigneeId, dueDate = undefined) {
      return ActionService.post('hit-action/assign', {
        values: {
          action_id: actionId,
          assignee_id: assigneeId,
          assigner_id: useUserProfileStore().staffId,
          due_date: dueDate,
        },
      });
    },

    /**
     * Unassigns the action via the Action API to make some checks first
     */
    async unAssignAction(actionId) {
      return ActionService.post(`hit-action/unassign/${actionId}`)
        .then((res) => {
          this.updateAssignedActions();
          return res;
        })
        .catch((_e) => {
          console.error('Error while unassigning action');
        });
    },

    /**
     * Starts the action via the Action API to make different checks
     */
    async startAction(actionId) {
      if (this.changingState) return;
      this.changingState = true;
      this.activeActionId = actionId;
      return ActionService.post(`hit-action/start/${actionId}`)
        .then(() => {
          this.updateAssignedActions();
        })
        .catch((e) => {
          console.error(`Error while starting action, ${e}`);
        });
    },

    /**
     * Pauses the action via the Action API to make different checks
     */
    async pauseAction(actionId) {
      if (this.changingState) return;
      this.changingState = true;
      return ActionService.post(`hit-action/pause/${actionId}`)
        .then(() => {
          this.updateAssignedActions();
        })
        .catch((e) => {
          console.error(`Error while pausing action, ${e}`);
        });
    },

    /**
     * Stops the action via the Action API to make different checks
     */
    async stopAction(actionId, message) {
      if (this.changingState) return;
      this.changingState = true;
      return ActionService.post(`hit-action/stop/${actionId}`, {
        values: {message: message || null},
      })
        .then(() => {
          this.updateAssignedActions();
        })
        .catch((e) => {
          console.error(`Error while stopping action, ${e}`);
        });
    },

    /**
     * Deletes the action via the Action API to make some checks before deleting it
     */
    async deleteAction(actionId) {
      return ActionService.post('hit-action/delete', {
        values: {actions_to_delete: [actionId]},
      })
        .then((res) => {
          this.updateAssignedActions();
          return res !== 'Error';
        })
        .catch((e) => {
          console.error(`Error while deleting action, ${e}`);
        });
    },

    /**
     * Closes the action via the Action API to make some checks before closing it
     */
    async closeAction(actionId) {
      return ActionService.post(`hit-action/close/${actionId}`)
        .then(() => {
          this.updateAssignedActions();
        })
        .catch((e) => {
          console.error(`Error while closing action, ${e}`);
        });
    },

    /**
     * Reopens the action via the Action API to make some checks before reopening it
     */
    async reopenAction(actionId) {
      return ActionService.post(`hit-action/reopen/${actionId}`)
        .then(() => {
          this.updateAssignedActions();
        })
        .catch((e) => {
          console.error(`Error while reopening action, ${e}`);
        });
    },

    /**
     * Creates record in action_tag table -> history record via trigger
     */
    async addActionTag(actionId, tagId) {
      await DataService.create('action_tag', {
        action_id: actionId,
        tag_id: tagId,
      });
      return this.updateAssignedActions();
    },

    /**
     * Removes the tag from the action -> history record via trigger
     */
    async removeActionTag(actionId, tagId) {
      await DataService.delete('action_tag', {
        action_id: `eq.${actionId}`,
        tag_id: `eq.${tagId}`,
      });
      return this.updateAssignedActions();
    },

    /**
     * Adds a comment to the action history
     */
    async comment(actionId, historyId, comment) {
      return DataService.upsert('action_history', {
        id: historyId,
        action_id: actionId,
        message: comment,
        operation: HitActionUtils.Operation.MESSAGE,
      });
    },

    /**
     * Renames the action in the database
     */
    async renameAction(actionId, newName, _updateAction = true) {
      if (this.isMyAction(actionId)) {
        await DataService.update(
          'action',
          {id: `eq.${actionId}`},
          {designation: newName},
        );
        return this.updateAssignedActions();
      } else {
        return DataService.update(
          'action',
          {id: `eq.${actionId}`},
          {designation: newName},
        );
      }
    },

    /**
     * Stores the last used filters otherwise they would disappear when the user changes the view
     */
    setActionFilter(filterString) {
      this.actionFilters = filterString;
      this.updateAssignedActions();
    },

    /**
     * Calculates the total amount of assigned actions of the user
     */
    getCountOfAssignedActions(staffId) {
      if (!staffId) return;
      DataService.read('action', {
        attributes: 'count()',
        filters: {
          assignee_id: `eq.${staffId}`,
          status: `eq.ASSIGNED`,
        },
      }).then((response) => {
        if (response && response.data && response.data.length > 0) {
          this.assignedCount = response.data[0].count;
        }
      });
    },

    /**
     * Starts the timer in the header
     */
    startChronometer() {
      this.setConsumedDuration();
      this.stopChronometer();
      this.updateChronometerElapsedTime();
      this.chronometerInterval = setInterval(() => {
        this.updateChronometerElapsedTime();
      }, 1000);
    },

    /**
     * Stops the counter in the header
     */
    stopChronometer() {
      clearInterval(this.chronometerInterval);
      this.chronometerInterval = null;
    },

    /**
     * Initialises the timer in the header with the previously consumed time
     */
    setConsumedDuration() {
      if (!this.activeCard) {
        this.chronometerStartedAtMs = null;
        this.chronometerBaseDurationMs = 0;
        this.elapsedTime = 0;
        return;
      }
      this.chronometerBaseDurationMs = this.activeCard.finished_duration ?? 0;
      this.chronometerStartedAtMs = this.activeCard.last_start
        ? this.activeCard.last_start.getTime()
        : null;
      this.updateChronometerElapsedTime();
    },

    /**
     * Calculates elapsed time from the exact start timestamp
     */
    updateChronometerElapsedTime() {
      const now = Date.now();
      const startedAt = this.chronometerStartedAtMs;
      const elapsedSinceStart = startedAt ? Math.max(0, now - startedAt) : 0;
      this.elapsedTime = this.chronometerBaseDurationMs + elapsedSinceStart;
    },

    /**
     * Updates the right column with the state of the assigned actions and also
     * updates the top bar with the running action because it is the same store
     */
    async updateAssignedActions() {
      this.stopChronometer();
      const staffId = useUserProfileStore().staffId;
      this.getCountOfAssignedActions(staffId);
      const basicFilters = {
        assignee_id: `eq.${staffId}`,
        status: 'eq.ASSIGNED',
      };
      const attributes = `id, status, no, designation, assignee_id!inner(id, user_id, first_name, last_name), creation_user_id, due_date, without_due_date, urgency, url, modification_date, importance,description,address_id,staff_id,equipment_id,project_part_id,document_id,resource_id,form_id,cost_id, sort_order, tag(id,designation,colour), action_history(operation, duration, operation_date),address(no,designation),staff!fk_action_staff_id_staff(no, designation),equipment(no,designation),resource(no,designation_${
        useConfigurationStore().userLanguageSnakeCase
      }),project_part(no,designation),project_part_item(no,designation),tagFilter:tag(),addressFilter:address(),staffFilter:staff!fk_action_staff_id_staff(),resourceFilter:resource(),equipmentFilter:equipment(),projectPartFilter:project_part(),projectPartItemFilter:project_part_item()`;
      const filters = {...basicFilters};

      const requests = [
        DataService.read('action', {
          attributes: attributes,
          order: 'sort_order.desc.nullsfirst',
          filters: basicFilters,
        }),
      ];
      let basicResponse = [],
        filteredActionIds = [];

      if (this.actionFilters) {
        const filterParts = this.actionFilters
          .split(' ')
          .filter((i) => i !== '');
        let designationString = '';
        filterParts.forEach((filter) => {
          designationString += `designation.ilike.*${filter}*,`;
        });
        filters['tagFilter.and'] = `(${designationString.slice(0, -1)})`;
        filters['addressFilter.and'] = `(${designationString.slice(0, -1)})`;
        filters['staffFilter.and'] = `(${designationString.slice(0, -1)})`;
        filters['equipmentFilter.and'] = `(${designationString.slice(0, -1)})`;
        filters['projectPartFilter.and'] = `(${designationString.slice(
          0,
          -1,
        )})`;
        filters['projectPartItemFilter.and'] = `(${designationString.slice(
          0,
          -1,
        )})`;
        const resourceString = designationString.replaceAll(
          'designation',
          `designation_${useConfigurationStore().userLanguageSnakeCase}`,
        );
        const noString = designationString.replaceAll('designation', 'no');
        filters['resourceFilter.and'] = `(${resourceString.slice(0, -1)})`;
        filters['or'] = `(and(${designationString.slice(
          0,
          -1,
        )}),and(${noString.slice(
          0,
          -1,
        )}),tagFilter.not.is.null,addressFilter.not.is.null,staffFilter.not.is.null,resourceFilter.not.is.null,equipmentFilter.not.is.null,projectPartFilter.not.is.null,projectPartItemFilter.not.is.null)`;
        requests.push(
          DataService.read('action', {
            attributes: attributes,
            order: 'sort_order.desc.nullsfirst',
            filters: filters,
          }),
        );
        [basicResponse, filteredActionIds] = await Promise.all(requests);
        [basicResponse, filteredActionIds] = [
          basicResponse.data,
          filteredActionIds.data.map((action) => action.id),
        ];
      } else {
        [basicResponse] = await Promise.all(requests);
        basicResponse = basicResponse.data;
        filteredActionIds = basicResponse.map((action) => action.id);
      }

      this.activeActionId = null;
      this.assignedActions = [];
      if (basicResponse.length > 0) {
        const actionCards = basicResponse.map((action) => {
          let totalDuration = 0;
          action.action_history.forEach((op) => {
            totalDuration += HitActionUtils.durationToMs(op.duration);
          });

          const lastOperation = action.action_history.reduce(
            (last, current) => {
              if (!['START', 'PAUSE', 'STOP'].includes(current.operation))
                return last;
              if (
                !last ||
                new Date(current.operation_date) > new Date(last.operation_date)
              ) {
                return current;
              }
              return last;
            },
            null,
          );

          const userLan = useConfigurationStore().userLanguageSnakeCase;
          let card;
          if (lastOperation?.operation === 'START') {
            card = actionToCard(
              action,
              totalDuration,
              'START',
              new Date(lastOperation.operation_date),
              userLan,
            );
          } else if (lastOperation?.operation === 'PAUSE') {
            card = actionToCard(action, totalDuration, 'PAUSE', null, userLan);
          } else {
            card = actionToCard(action, totalDuration, 'STOP', null, userLan);
          }
          card.last_operation_date = lastOperation?.operation_date
            ? new Date(lastOperation.operation_date)
            : null;
          return card;
        });

        const sortedActions = sortActions('status', actionCards);
        this.assignedActions = sortedActions?.assigned ?? [];
        this.actionStack = [];
        let latestStartCard = null;
        let latestStartDate = null;
        let latestAnyRunningCard = null;
        let latestAnyRunningDate = null;
        this.assignedActions.forEach((action) => {
          if (['START', 'PAUSE'].includes(action.running_state)) {
            this.actionStack.push(action.id);
            const opDate = action.last_operation_date || null;
            if (action.running_state === 'START') {
              if (!latestStartDate || (opDate && opDate > latestStartDate)) {
                latestStartCard = action;
                latestStartDate = opDate;
              }
            }
            if (
              !latestAnyRunningDate ||
              (opDate && opDate > latestAnyRunningDate)
            ) {
              latestAnyRunningCard = action;
              latestAnyRunningDate = opDate;
            }
          }
        });
        if (latestStartCard) {
          this.activeActionId = latestStartCard.id;
          this.startChronometer();
        } else if (latestAnyRunningCard) {
          this.activeActionId = latestAnyRunningCard.id;
          this.setConsumedDuration();
        }
      }
      this.changingState = false;
      this.filteredActionIds = filteredActionIds;
    },
  },
});
